import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Permission-aware module sidebar.
 *
 * Each item declares either `perm` (string | array) or `roles`.
 * Items the user cannot see are hidden entirely. Backend still enforces
 * on every API call.
 *
 * Items without `perm` are shown to all authenticated users (public
 * module landing pages that themselves gate their inner actions).
 */
const MODULES = [
  { to: "/app/dashboard",  label: "Dashboard",  testid: "nav-dashboard" },
  { to: "/app/inventory",  label: "Inventory",  testid: "nav-inventory" },
  { to: "/app/parties",    label: "Parties",    testid: "nav-parties" },
  { to: "/app/orders",     label: "Orders",     perm: "order.view", testid: "nav-orders" },
  { to: "/app/sales",      label: "Sales",      perm: "sales.view", testid: "nav-sales" },
  { to: "/app/inventory/stock", label: "Stock",   testid: "nav-stock" },
  { to: "/app/sales/new-invoice", label: "New invoice", perm: "document.create", testid: "nav-new-invoice" },
  { to: "/app/purchases",  label: "Purchases",  testid: "nav-purchases" },
  { to: "/app/approvals",  label: "Approvals",  perm: ["portal.request.view", "portal.order.approve"], testid: "nav-approvals" },
  { to: "/app/settings",   label: "Settings",   roles: ["admin"], testid: "nav-settings" },
];

export function Sidebar({ testid = "app-sidebar" }) {
  const { user, hasPermission } = useAuth();

  const items = MODULES.filter((m) => {
    if (m.perm && !hasPermission(m.perm)) return false;
    if (m.roles && !m.roles.includes(user?.role)) return false;
    return true;
  });

  return (
    <aside
      data-testid={testid}
      className="hidden md:flex md:w-56 flex-col border-r border-[var(--phx-slate-200)] bg-white"
    >
      <div className="px-6 py-5 border-b border-[var(--phx-slate-200)] flex items-center gap-3">
        <div className="w-6 h-6 bg-[var(--phx-brand-ink)]" />
        <span className="text-[10px] uppercase tracking-[var(--phx-tracking)] font-semibold">Phoenix</span>
      </div>
      <nav className="flex-1 py-4">
        {items.map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            data-testid={m.testid}
            className={({ isActive }) =>
              `block px-6 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[var(--phx-slate-100)] text-[var(--phx-brand-primary)] border-l-2 border-[var(--phx-brand-primary)]"
                  : "text-[var(--phx-slate-700)] hover:bg-[var(--phx-slate-50)] border-l-2 border-transparent"
              }`
            }
          >
            {m.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
